/**
 * SSE (Server-Sent Events) client for consuming AI proxy streams from the backend.
 * 
 * The backend sends events in the format:
 *   event: text|reasoning|usage|finalText|error|done
 *   data: <payload>
 * 
 * This module provides a helper to consume those streams with callbacks.
 */
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { AI_API_BASE } from './apiConfig';

const isNativeHttpTarget = (url) => {
  try {
    return Capacitor.isNativePlatform() && /^https?:\/\//i.test(url);
  } catch {
    return false;
  }
};

const dispatchSseMessages = (payload, callbacks = {}) => {
  if (!payload) return;

  const messages = payload.split(/\r?\n\r?\n/);

  for (const message of messages) {
    if (!message.trim()) continue;

    let eventType = 'message';
    const dataLines = [];

    for (const line of message.split(/\r?\n/)) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        dataLines.push(line.slice(6));
      }
    }

    const data = dataLines.join('\n');

    switch (eventType) {
      case 'text':
        try { callbacks.onText?.(JSON.parse(data)); } catch { callbacks.onText?.(data); }
        break;
      case 'reasoning':
        try { callbacks.onReasoning?.(JSON.parse(data)); } catch { callbacks.onReasoning?.(data); }
        break;
      case 'usage':
        try { callbacks.onUsage?.(JSON.parse(data)); } catch {}
        break;
      case 'finalText':
        try { callbacks.onFinalText?.(JSON.parse(data)); } catch { callbacks.onFinalText?.(data); }
        break;
      case 'error':
        try {
          callbacks.onError?.(JSON.parse(data));
        } catch {
          callbacks.onError?.({ message: data });
        }
        break;
      case 'done':
        return;
      default:
        break;
    }
  }
};

/**
 * Make a streaming request to the AI proxy backend.
 * Parses SSE events and calls the appropriate callbacks.
 *
 * @param {string} endpoint - Relative path (e.g. '/completion')
 * @param {Object} body - JSON request body
 * @param {Object} callbacks
 * @param {Function} [callbacks.onText] - Called for each text delta chunk
 * @param {Function} [callbacks.onReasoning] - Called for reasoning delta
 * @param {Function} [callbacks.onUsage] - Called with usage object when stream ends
 * @param {Function} [callbacks.onFinalText] - Called with authoritative full text (OpenAI)
 * @param {Function} [callbacks.onError] - Called on stream error
 * @param {AbortSignal} [signal] - AbortSignal for cancellation
 * @returns {Promise<void>}
 */
export const streamFromBackend = async (endpoint, body, callbacks = {}, signal) => {
  const url = `${AI_API_BASE}${endpoint}`;

  if (isNativeHttpTarget(url)) {
    const response = await CapacitorHttp.request({
      url,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      responseType: 'text',
      connectTimeout: 120000,
      readTimeout: 120000,
    });

    if (signal?.aborted) {
      throw new Error('Request aborted');
    }

    if (response.status < 200 || response.status >= 300) {
      throw new Error(`AI proxy error ${response.status}: ${response.data || ''}`);
    }

    dispatchSseMessages(String(response.data || ''), callbacks);
    return;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`AI proxy error ${response.status}: ${errorBody}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Process complete SSE messages (double newline separated)
    const messages = buffer.split('\n\n');
    // Keep the last potentially incomplete chunk in the buffer
    buffer = messages.pop() || '';

    for (const message of messages) {
      if (!message.trim()) continue;
      dispatchSseMessages(message, callbacks);
    }
  }

  if (buffer.trim()) {
    dispatchSseMessages(buffer, callbacks);
  }
};

/**
 * Make a non-streaming JSON request to the AI proxy backend.
 *
 * @param {string} endpoint - Relative path (e.g. '/image')
 * @param {Object} body - JSON request body
 * @returns {Promise<Object>} Parsed JSON response
 */
export const fetchFromBackend = async (endpoint, body) => {
  const url = `${AI_API_BASE}${endpoint}`;

  if (isNativeHttpTarget(url)) {
    const response = await CapacitorHttp.request({
      url,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      responseType: 'json',
      connectTimeout: 120000,
      readTimeout: 120000,
    });

    if (response.status < 200 || response.status >= 300) {
      throw new Error(`AI proxy error ${response.status}: ${JSON.stringify(response.data || {})}`);
    }

    return response.data;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`AI proxy error ${response.status}: ${errorBody}`);
  }

  return response.json();
};
